import { ApiError } from './errors';
import { getAccessToken, refreshAccessToken, setAccessToken } from './tokens';

export interface ApiClientOptions {
  baseURL: string;
  /** Called on terminal 401 (refresh failed) so apps can navigate to /login. */
  onUnauthorized?: () => void;
}

export type QueryValue = string | number | boolean | undefined | null;
export type Query = Readonly<Record<string, QueryValue>>;

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the auto-attach Authorization header. */
  anonymous?: boolean;
}

/**
 * Lightweight typed fetch wrapper with:
 *   - JSON in / JSON out
 *   - automatic Bearer attach
 *   - single-flight refresh on 401
 *   - typed error envelope (ApiError)
 *   - request-id propagation when the server set one
 */
export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  get baseURL(): string {
    return this.opts.baseURL;
  }

  async request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const url = this.buildURL(path, options.query);
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(options.headers ?? {}),
    };
    let body: BodyInit | undefined;
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
    if (!options.anonymous) {
      const tok = getAccessToken();
      if (tok) headers.Authorization = `Bearer ${tok}`;
    }

    const res = await fetch(url, {
      ...options,
      method,
      headers,
      body,
      credentials: 'include',
    });

    if (res.status === 401 && !options.anonymous) {
      const refreshed = await refreshAccessToken(this.opts.baseURL);
      if (refreshed) {
        headers.Authorization = `Bearer ${refreshed}`;
        const retry = await fetch(url, {
          ...options,
          method,
          headers,
          body,
          credentials: 'include',
        });
        return this.parse<T>(retry);
      }
      setAccessToken(null);
      this.opts.onUnauthorized?.();
      throw await ApiError.fromResponse(res);
    }

    return this.parse<T>(res);
  }

  private async parse<T>(res: Response): Promise<T> {
    if (!res.ok) throw await ApiError.fromResponse(res);
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('content-type') ?? '';
    if (ct.includes('application/json')) return (await res.json()) as T;
    return (await res.text()) as unknown as T;
  }

  private buildURL(path: string, query?: RequestOptions['query']): string {
    const base = this.opts.baseURL.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${base}${p}`, window.location.origin);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options);
  }
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body });
  }
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body });
  }
  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }
}

let singleton: ApiClient | null = null;

export function configureApi(opts: ApiClientOptions): ApiClient {
  singleton = new ApiClient(opts);
  return singleton;
}

export function api(): ApiClient {
  if (!singleton) {
    throw new Error('configureApi() must be called before api()');
  }
  return singleton;
}
